import pc from "picocolors";

/**
 * Error context information for brick-codegen errors
 */
export interface ErrorContext {
  /** Absolute path to the *.brick.ts file */
  filePath?: string;
  /** Module name */
  moduleName?: string;
  /** Method/property name where error occurred */
  memberName?: string;
  /** Parameter name where error occurred */
  parameterName?: string;
  /** Line number in source file (1-indexed) */
  line?: number;
  /** Column number in source file (1-indexed) */
  column?: number;
  /** Additional context details */
  details?: string;
}

/**
 * Custom error class for brick-codegen with rich context information
 */
export class BrickCodegenError extends Error {
  public readonly context: ErrorContext;
  public readonly originalError?: Error;

  constructor(
    message: string,
    context: ErrorContext = {},
    originalError?: Error
  ) {
    super(message);
    this.name = "BrickCodegenError";
    this.context = context;
    this.originalError = originalError;

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BrickCodegenError);
    }
  }

  /**
   * Create a formatted error message with context
   */
  public toFormattedString(useColors: boolean = true): string {
    const parts: string[] = [];

    // File location
    if (this.context.filePath) {
      const location = this.context.filePath;
      const lineCol =
        this.context.line !== undefined
          ? `:${this.context.line}${
              this.context.column !== undefined ? `:${this.context.column}` : ""
            }`
          : "";
      parts.push(`File: ${location}${lineCol}`);
    }

    // Module context
    if (this.context.moduleName) {
      parts.push(`Module: ${this.context.moduleName}`);
    }

    // Member context
    if (this.context.memberName) {
      const memberType = this.context.parameterName ? "Method" : "Property";
      parts.push(`${memberType}: ${this.context.memberName}`);
    }

    // Parameter context
    if (this.context.parameterName) {
      parts.push(`Parameter: ${this.context.parameterName}`);
    }

    // Location info
    if (this.context.line !== undefined && !this.context.filePath) {
      const location = `Line ${this.context.line}${
        this.context.column !== undefined
          ? `, Column ${this.context.column}`
          : ""
      }`;
      parts.push(location);
    }

    // Error message
    parts.push("");
    parts.push(`Error: ${this.message}`);

    // Additional details
    if (this.context.details) {
      parts.push("");
      parts.push(this.context.details);
    }

    return parts.join("\n  ");
  }

  /**
   * Get a clickable file location string for terminals
   */
  public getFileLocation(): string | undefined {
    if (!this.context.filePath) {
      return undefined;
    }

    let location = this.context.filePath;
    if (this.context.line !== undefined) {
      location += `:${this.context.line}`;
      if (this.context.column !== undefined) {
        location += `:${this.context.column}`;
      }
    }
    return location;
  }
}

/**
 * Format multiple errors into a user-friendly output with colors
 */
export function formatErrors(errors: BrickCodegenError[]): string {
  if (errors.length === 0) {
    return "";
  }

  const separator = pc.gray("─".repeat(70));
  const formattedErrors = errors
    .map((error, index) => {
      const parts: string[] = [];

      // Header with file path
      const fileName = error.context.filePath || "unknown file";
      parts.push(pc.bold(pc.cyan(`\n${index + 1}. Error in ${fileName}`)));

      // Location (clickable in most terminals)
      const location = error.getFileLocation();
      if (location) {
        parts.push(pc.dim(`   at ${location}`));
      }

      parts.push(""); // Empty line

      // Module context
      if (error.context.moduleName) {
        parts.push(pc.blue(`   Module: ${error.context.moduleName}`));
      }

      // Member context
      if (error.context.memberName) {
        const memberType = error.context.parameterName ? "Method" : "Member";
        parts.push(pc.blue(`   ${memberType}: ${error.context.memberName}`));
      }

      // Parameter context
      if (error.context.parameterName) {
        parts.push(pc.blue(`   Parameter: ${error.context.parameterName}`));
      }

      // Error message
      parts.push("");
      parts.push(pc.red(`   ✗ ${error.message}`));

      // Additional details
      if (error.context.details) {
        parts.push("");
        parts.push(pc.dim(`   ${error.context.details}`));
      }

      return parts.join("\n");
    })
    .join(`\n${separator}\n`);

  return formattedErrors;
}

/**
 * Create error summary statistics
 */
export function getErrorSummary(
  errors: BrickCodegenError[],
  successCount: number
): string {
  const errorCount = errors.length;
  const fileSet = new Set(
    errors.map((e) => e.context.filePath).filter((f): f is string => !!f)
  );
  const fileCount = fileSet.size;

  const parts: string[] = [];

  if (errorCount > 0) {
    parts.push(
      `Found ${errorCount} error${errorCount === 1 ? "" : "s"}${
        fileCount > 0
          ? ` in ${fileCount} file${fileCount === 1 ? "" : "s"}`
          : ""
      }.`
    );
  }

  if (successCount > 0) {
    parts.push(
      `${successCount} module${
        successCount === 1 ? "" : "s"
      } generated successfully.`
    );
  }

  return parts.join(" ");
}

/**
 * Helper to wrap any error with context
 */
export function wrapError(
  error: unknown,
  context: ErrorContext,
  message?: string
): BrickCodegenError {
  if (error instanceof BrickCodegenError) {
    // Merge contexts
    return new BrickCodegenError(
      message || error.message,
      { ...error.context, ...context },
      error.originalError
    );
  }

  const errorMessage =
    message || (error instanceof Error ? error.message : String(error));
  return new BrickCodegenError(
    errorMessage,
    context,
    error instanceof Error ? error : undefined
  );
}

/**
 * Parse React Native Codegen error and extract useful information
 */
export function parseReactNativeCodegenError(errorMessage: string): {
  type: string;
  module: string;
  property?: string;
  message: string;
  details?: string;
} | null {
  // Pattern: [Codegen] ErrorType: Module ModuleName: message
  const codegenPattern = /\[Codegen\]\s+(\w+):\s+Module\s+(\w+):\s+(.+)/;
  const match = errorMessage.match(codegenPattern);

  if (!match) {
    return null;
  }

  const [, errorType, moduleName, restMessage] = match;

  // Try to extract property name from error message
  let propertyName: string | undefined;
  const cleanMessage = restMessage || "";
  let details: string | undefined;

  // Pattern: property 'PropertyName'
  const propertyPattern = /property\s+'([^']+)'/;
  const propMatch = restMessage?.match(propertyPattern);
  if (propMatch) {
    propertyName = propMatch[1];
  }

  return {
    type: errorType || "CodegenError",
    module: moduleName || "Unknown",
    property: propertyName,
    message: cleanMessage,
    details,
  };
}

/**
 * Create a BrickCodegenError from React Native Codegen error
 */
export function createCodegenError(
  errorMessage: string,
  specFilePath?: string,
  sourceMap?: import("./source-map").SourceMapRegistry
): BrickCodegenError {
  const parsed = parseReactNativeCodegenError(errorMessage);

  if (!parsed) {
    // Fallback for unparseable errors
    return new BrickCodegenError(
      `React Native Codegen failed: ${errorMessage}`,
      { filePath: specFilePath }
    );
  }

  // Try to map back to original source using source map
  let finalContext: ErrorContext = {
    filePath: specFilePath,
    moduleName: parsed.module,
    memberName: parsed.property,
    details: parsed.details,
  };

  if (sourceMap && parsed.property) {
    const mapping = sourceMap.findMapping(parsed.property);
    if (mapping) {
      // Found original source - use that instead
      finalContext = {
        filePath: mapping.sourceFile,
        moduleName: mapping.moduleName,
        memberName: mapping.sourceName,
        line: mapping.sourceLine,
        column: mapping.sourceColumn,
        details: `Invalid event definition: '${mapping.sourceName}'

The event '${mapping.sourceName}' was converted to '${
          mapping.generatedName
        }' but React Native
Codegen requires EventEmitter properties with concrete types.

Use CodegenTypes.EventEmitter instead:
  readonly on${mapping.sourceName
    .charAt(0)
    .toUpperCase()}${mapping.sourceName.slice(1)}: CodegenTypes.EventEmitter<{ /* payload */ }>;`,
      };
    }
  }

  return new BrickCodegenError(
    `[React Native Codegen] ${parsed.type}: ${parsed.message}`,
    finalContext
  );
}
